import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import {
  GridRowModesModel,
  GridRowModes,
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridActionsCellItem,
  GridEventListener,
  GridRowId,
  GridRowModel,
  GridRowEditStopReasons,
  GridToolbarProps,
} from "@mui/x-data-grid";
import { FormikProps } from "formik";
import { useQuery } from "@tanstack/react-query";
import { API_URL } from "src/utilities";
import { useAuth } from "src/context/auth";
import { IUserInfo } from "src/interface/IUserInfo";
import {
  ListOfTypeUserInfo,
  TypeUserInfo,
} from "src/interface/enum/TypeUserInfo";
import { IAppUser } from "src/interface/IUser";
import { EditToolbarUserInfo } from "./EditToolbarUserInfo";

export default function UserInfo({
  onClose,
  userData,
  refetchUser,
}: {
  userData?: IAppUser;
  onClose: () => void;
  refetchUser: () => void;
}) {
  const { user } = useAuth();
  const [rows, setRows] = React.useState<IUserInfo[]>([]);

  const { data: customersData } = useQuery({
    queryKey: ["CustomersData"],
    queryFn: () =>
      fetch(API_URL + "user/getByRole/Customer", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${user?.token}`,
          Aceept: "application/json",
        },
      }).then((res) => res.json()),
  });

  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {}
  );

  const handleRowEditStop: GridEventListener<"rowEditStop"> = (
    params,
    event
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };
  const handleDeleteClick = (id: GridRowId) => () => {
    setRows(rows?.filter((row: any) => row.id !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows?.find((row: any) => row.id === id);
    if (editedRow?.isNew) {
      setRows(rows?.filter((row: any) => row.id !== id)); // حذف ردیف جدید در صورت انصراف
    }
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false }; // علامت‌گذاری ردیف به عنوان "نه جدید"

    setRows((oldRows: any) =>
      oldRows?.map((row: any) =>
        row.id === newRow.id ? (updatedRow as IUserInfo) : row
      )
    );

    return updatedRow;
  };
  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const columns: GridColDef[] = [
    {
      field: "type",
      headerName: "عنوان",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
      type: "singleSelect",
      valueOptions: ListOfTypeUserInfo,
    },
    {
      field: "caption",
      headerName: "مقدار",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },
    {
      field: "postalCode",
      headerName: "کد پستی",
      minWidth: 150,
      filterable: true,
      align: "center",
      headerAlign: "center",
      flex: 1,
      editable: true,
      sortable: true,
    },

    {
      field: "actions",
      type: "actions",
      headerName: "عملیات",
      width: 100,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;
        if (isInEditMode) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              sx={{
                color: "primary.main",
              }}
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="انصراف"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="ویرایش"
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="حذف"
            onClick={handleDeleteClick(id)}
            color="inherit"
          />,
        ];
      },
    },
  ];
  React.useEffect(() => {
    if (
      userData &&
      userData.addressesUser &&
      userData.addressesUser.length > 0
    ) {
      setRows(userData.addressesUser ?? []);
    }
  }, [userData]);
  return (
    <Box
      sx={{
        height: "auto",
        width: "100%",
        "& .actions": {
          color: "text.secondary",
        },
        "& .textPrimary": {
          color: "text.primary",
        },
      }}
    >
      <DataGrid
        rows={rows}
        columns={columns}
        editMode="row"
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        slots={{
          toolbar: (props) => (
            <EditToolbarUserInfo
              onClose={onClose}
              userRef={userData?.id}
              existingUserInfo={userData?.addressesUser}
              setUpdatingUserInfo={setRows}
              updatingUserInfo={rows}
              rows={props.rows}
              setRowModesModel={props.setRowModesModel}
              setRows={props.setRows}
              refetchUser={refetchUser}
            />
          ),
        }}
        slotProps={{
          //@ts-ignore
          toolbar: { setRows, setRowModesModel, rows },
        }}
      />
    </Box>
  );
}
